import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, RefreshCw, KeyRound, ShieldCheck, Pencil, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import OperatoreFoto from "../components/OperatoreFoto";
import OperatoreSchedaView from "../components/OperatoreSchedaView";
import OperatorePermDialog from "../components/OperatorePermDialog";
import Documenti from "../components/Documenti";
import BustePaga from "../components/BustePaga";
import { MODULES } from "../config/modules";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

export default function SchedaOperatore() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { structures = [], can } = useAuth();
  const [pinInput, setPinInput] = useState("");
  const [showPerm, setShowPerm] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const canManageAccess = can && (can("utenti") || can("operatori"));
  const { data: o, isLoading } = useQuery({ queryKey: ["operatore", id], queryFn: async () => (await api.get(`/operatori/${id}`)).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["operatore", id] });
  const opFields = MODULES.operatori.fields;
  const openEdit = () => { setForm({ ...o, structure_ids: (o.structure_ids && o.structure_ids.length) ? o.structure_ids : (o.structure_id ? [o.structure_id] : []) }); setEditOpen(true); };
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const saveEdit = async () => {
    if (!form.nome || !form.cognome) return toast.error("Nome e cognome obbligatori");
    try {
      const payload = { ...form };
      if (Array.isArray(payload.structure_ids) && payload.structure_ids.length) payload.structure_id = payload.structure_ids[0];
      await api.put(`/operatori/${id}`, payload);
      toast.success("Operatore aggiornato"); setEditOpen(false); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); }
  };

  const savePin = async () => {
    try { await api.put(`/operatori/${id}`, { pin: pinInput }); toast.success("PIN aggiornato"); setPinInput(""); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio del PIN"); }
  };
  const regenPin = async () => {
    try { const { data } = await api.post(`/firma/regenerate-pin/${id}`); toast.success(`Nuovo PIN generato: ${data.pin}`); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore nella generazione del PIN"); }
  };

  if (isLoading || !o) return <div className="text-muted-foreground">Caricamento...</div>;

  const topBar = (
    <div className="mb-5 p-4 rounded-xl border bg-emerald-50/60 flex flex-wrap items-center gap-4" data-testid="op-pin-box">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-emerald-600 text-white grid place-items-center"><KeyRound size={20} /></div>
        <div>
          <div className="text-xs font-semibold text-muted-foreground">PIN Firma Rapida</div>
          <button type="button" onClick={() => setShowPin((v) => !v)} title={showPin ? "Nascondi PIN" : "Mostra PIN"} data-testid="op-pin-toggle"
            className="flex items-center gap-2 text-2xl font-head font-bold tabnum hover:text-primary transition-colors">
            <span data-testid="op-pin-value">{o.pin ? (showPin ? o.pin : "•".repeat(String(o.pin).length)) : "non impostato"}</span>
            {o.pin && (showPin ? <EyeOff size={16} className="text-muted-foreground" /> : <Eye size={16} className="text-muted-foreground" />)}
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:ml-auto">
        <Input value={pinInput} onChange={(e) => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
          maxLength={4} placeholder="es. 42" className="w-24 text-center font-bold" data-testid="op-pin-input" />
        <Button onClick={savePin} disabled={pinInput.length !== 2 && pinInput.length !== 4} data-testid="op-pin-save">Imposta</Button>
        <Button variant="outline" onClick={regenPin} data-testid="op-pin-regen"><RefreshCw size={15} className="mr-1" /> Rigenera</Button>
        <Button variant="outline" onClick={openEdit} data-testid="op-modifica"><Pencil size={15} className="mr-1" /> Modifica</Button>
        {canManageAccess && <Button variant="outline" onClick={() => setShowPerm(true)} data-testid="op-manage-access"><ShieldCheck size={15} className="mr-1" /> Gestisci accessi</Button>}
      </div>
    </div>
  );

  const coop = (o.contratto || "").toLowerCase().includes("cooperativ");
  const documentiNode = (smart) => (
    <Documenti ownerType="operatore" ownerId={o.id} ownerNome={`${o.nome} ${o.cognome}`} structureId={o.structure_id} smart={smart}
      categories={["Contratto di lavoro completo", "Carta d'identità", "Patente", "Tessera sanitaria", "Codice fiscale", "Curriculum", "Idoneità sanitaria", "Altro"]}
      obbligatori={coop ? [] : [["Contratto di lavoro completo", ["Contratto di lavoro"], "Il contratto firmato, tutte le pagine."]]}
      docFilter={(d) => d.categoria !== "Attestato"} defaultCategoria="Carta d'identità" testidSuffix="doc" />
  );
  const attestatiNode = (smart) => (
    <Documenti ownerType="operatore" ownerId={o.id} ownerNome={`${o.nome} ${o.cognome}`} structureId={o.structure_id} smart={smart}
      categories={["Attestato"]} docFilter={(d) => d.categoria === "Attestato"} defaultCategoria="Attestato" testidSuffix="att" />
  );
  const bustePagaNode = <BustePaga operatoreId={o.id} operatoreNome={`${o.nome} ${o.cognome}`} structureId={o.structure_id} dataAssunzione={o.data_assunzione} cooperativa={coop} />;

  return (
    <div data-testid="scheda-operatore-page">
      <Button variant="ghost" onClick={() => nav("/operatori")} className="mb-3" data-testid="back-operatori"><ArrowLeft size={16} className="mr-1" /> Torna agli operatori</Button>
      <OperatoreSchedaView
        op={o} opId={o.id} structures={structures} manage
        foto={<OperatoreFoto operatore={o} onChanged={refresh} />}
        topBar={topBar}
        documentiNode={documentiNode}
        attestatiNode={attestatiNode}
        extraTabs={[{ value: "buste", label: "Buste paga", testid: "tab-op-buste", node: bustePagaNode }]}
      />
      <OperatorePermDialog operatore={o} open={showPerm} onOpenChange={setShowPerm} onSaved={refresh} />

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="op-edit-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil size={18} /> Modifica operatore</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {opFields.map((f) => (
              <div key={f.key} className={f.type === "textarea" || f.type === "strutture" ? "sm:col-span-2" : ""}>
                <Label className="text-xs font-semibold mb-1 block">{f.label}{f.req && " *"}</Label>
                {f.type === "textarea" ? (
                  <Textarea value={form[f.key] || ""} onChange={(e) => setF(f.key, e.target.value)} data-testid={`opf-${f.key}`} />
                ) : f.type === "select" ? (
                  <select value={form[f.key] || ""} onChange={(e) => setF(f.key, e.target.value)} className="h-10 w-full rounded-md border px-3 text-sm" data-testid={`opf-${f.key}`}>
                    <option value="">Seleziona…</option>
                    {f.options.map((op) => { const [v, l] = Array.isArray(op) ? op : [op, op]; return <option key={v} value={v}>{l}</option>; })}
                  </select>
                ) : f.type === "strutture" ? (
                  <div className="flex flex-wrap gap-1.5" data-testid={`opf-${f.key}`}>
                    {structures.filter((s) => s.id !== "all").map((s) => {
                      const cur = form.structure_ids || [];
                      const sel = cur.includes(s.id);
                      return <button type="button" key={s.id} onClick={() => setForm((p) => { const c = p.structure_ids || []; const next = sel ? c.filter((x) => x !== s.id) : [...c, s.id]; return { ...p, structure_ids: next }; })}
                        className={`text-xs font-semibold rounded-full border px-3 py-1 transition-colors ${sel ? "bg-primary text-white border-primary" : "hover:bg-secondary"}`}>{s.nome}</button>;
                    })}
                  </div>
                ) : (
                  <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={form[f.key] || ""} placeholder={f.placeholder || ""} onChange={(e) => setF(f.key, e.target.value)} data-testid={`opf-${f.key}`} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button onClick={saveEdit} className="font-semibold" data-testid="op-edit-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
